import type { Stripe } from "stripe";

import {
  PriceDiscoveryVersion,
  ViewWLSubscriptionsTiers,
} from "../models/subscriptions";
import { formatDollarPriceDisplay } from "./common";

/** Matches WebUser signup fetch default (`getSubscriptionListStripe("version2")`). */
export const WEB_SIGNUP_LATEST_PRICE_VERSION: PriceDiscoveryVersion = "version2";

export const WEB_SIGNUP_BOOTSTRAP_PARAMS = {
  price: "suPrice",
  priceId: "suPriceId",
  subId: "suSubId",
  name: "suName",
  entries: "suEntries",
  rewardPoints: "suRewardPoints",
  priceVersion: "suPriceVersion",
  couponId: "suCouponId",
  amountOff: "suAmountOff",
  percentOff: "suPercentOff",
  noCoupon: "suNoCoupon",
  promo: "promo",
} as const;

export interface WebSignupBootstrapCoupon {
  id: string;
  amount_off: number | null;
  percent_off: number | null;
}

export interface WebSignupBootstrapSubscription {
  docId: string;
  name: string;
  price: number;
  stripePriceId: string;
  entriesPerMonth: number;
  rewardPointsPerMonth: number;
  alternativePriceVersion: PriceDiscoveryVersion;
}

export interface WebSignupBootstrap {
  subscription: WebSignupBootstrapSubscription;
  /** Settled Stripe coupon for Elements amount; null = no Stripe discount. */
  coupon: WebSignupBootstrapCoupon | null;
  /** Optional WL / Stripe promotion code string (may be empty). */
  promoCode: string;
}

export interface BuildWebSignupBootstrapInput {
  subscription: WebSignupBootstrapSubscription;
  coupon?: WebSignupBootstrapCoupon | null;
  promoCode?: string;
}

export interface GenerateWebSignupUrlInput {
  baseUrl: string;
  subscriptionKey: string;
  subscription: WebSignupBootstrapSubscription;
  coupon?: WebSignupBootstrapCoupon | null;
  promoCode?: string;
}

const parsePositiveNumber = (value: string | null): number | null => {
  if (value === null || value.trim() === "") {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
};

const parseOptionalNumber = (value: string | null): number | null => {
  if (value === null || value.trim() === "") {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
};

/**
 * Returns a complete bootstrap payload only when all required mount fields are
 * present and well-typed. Otherwise null (caller falls back to API path).
 */
export const parseWebSignupBootstrap = (
  search: string,
): WebSignupBootstrap | null => {
  try {
    const params = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search,
    );

    const price = parsePositiveNumber(
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.price),
    );
    const stripePriceId = (
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.priceId) || ""
    ).trim();
    const docId = (params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.subId) || "").trim();
    const name = (params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.name) || "").trim();
    const entriesPerMonth = parseOptionalNumber(
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.entries),
    );
    const rewardPointsPerMonth = parseOptionalNumber(
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.rewardPoints),
    );
    const priceVersionRaw = (
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.priceVersion) || ""
    ).trim();
    const alternativePriceVersion =
      priceVersionRaw === "version1" || priceVersionRaw === "version2"
        ? priceVersionRaw
        : null;

    if (
      price === null ||
      !stripePriceId ||
      !docId ||
      !name ||
      entriesPerMonth === null ||
      rewardPointsPerMonth === null ||
      alternativePriceVersion === null
    ) {
      return null;
    }

    const noCoupon =
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.noCoupon) === "1" ||
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.noCoupon) === "true";

    const couponId = (
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.couponId) || ""
    ).trim();
    const amountOff = parseOptionalNumber(
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.amountOff),
    );
    const percentOff = parseOptionalNumber(
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.percentOff),
    );

    let coupon: WebSignupBootstrapCoupon | null = null;

    if (noCoupon) {
      coupon = null;
    } else if (couponId) {
      const hasAmountOff = amountOff !== null;
      const hasPercentOff = percentOff !== null;
      if (!hasAmountOff && !hasPercentOff) {
        return null;
      }
      coupon = {
        id: couponId,
        amount_off: hasAmountOff ? amountOff : null,
        percent_off: hasPercentOff ? percentOff : null,
      };
    } else {
      // Coupon state must be explicitly settled (suNoCoupon or coupon fields).
      return null;
    }

    const promoCode = (
      params.get(WEB_SIGNUP_BOOTSTRAP_PARAMS.promo) || ""
    )
      .trim()
      .toUpperCase();

    return {
      subscription: {
        docId,
        name,
        price,
        stripePriceId,
        entriesPerMonth,
        rewardPointsPerMonth,
        alternativePriceVersion,
      },
      coupon,
      promoCode,
    };
  } catch {
    return null;
  }
};

export const buildWebSignupBootstrapParams = (
  input: BuildWebSignupBootstrapInput,
): URLSearchParams => {
  const params = new URLSearchParams();
  const { subscription, coupon = null, promoCode = "" } = input;

  params.set(WEB_SIGNUP_BOOTSTRAP_PARAMS.price, String(subscription.price));
  params.set(WEB_SIGNUP_BOOTSTRAP_PARAMS.priceId, subscription.stripePriceId);
  params.set(WEB_SIGNUP_BOOTSTRAP_PARAMS.subId, subscription.docId);
  params.set(WEB_SIGNUP_BOOTSTRAP_PARAMS.name, subscription.name);
  params.set(
    WEB_SIGNUP_BOOTSTRAP_PARAMS.entries,
    String(subscription.entriesPerMonth),
  );
  params.set(
    WEB_SIGNUP_BOOTSTRAP_PARAMS.rewardPoints,
    String(subscription.rewardPointsPerMonth),
  );
  params.set(
    WEB_SIGNUP_BOOTSTRAP_PARAMS.priceVersion,
    subscription.alternativePriceVersion,
  );

  if (coupon === null || !coupon.id) {
    params.set(WEB_SIGNUP_BOOTSTRAP_PARAMS.noCoupon, "1");
  } else {
    params.set(WEB_SIGNUP_BOOTSTRAP_PARAMS.couponId, coupon.id);
    if (coupon.amount_off !== null && coupon.amount_off !== undefined) {
      params.set(
        WEB_SIGNUP_BOOTSTRAP_PARAMS.amountOff,
        String(coupon.amount_off),
      );
    }
    if (coupon.percent_off !== null && coupon.percent_off !== undefined) {
      params.set(
        WEB_SIGNUP_BOOTSTRAP_PARAMS.percentOff,
        String(coupon.percent_off),
      );
    }
  }

  const normalizedPromo = (promoCode || "").trim().toUpperCase();
  if (normalizedPromo) {
    params.set(WEB_SIGNUP_BOOTSTRAP_PARAMS.promo, normalizedPromo);
  }

  return params;
};

export const generateWebSignupUrl = (
  input: GenerateWebSignupUrlInput,
): string => {
  const baseUrl = input.baseUrl.replace(/\/$/, "");
  const subscriptionKey = input.subscriptionKey.trim().toLowerCase();
  const params = buildWebSignupBootstrapParams({
    subscription: input.subscription,
    coupon: input.coupon ?? null,
    promoCode: input.promoCode,
  });
  const query = params.toString();
  return `${baseUrl}/join/${encodeURIComponent(subscriptionKey)}${
    query ? `?${query}` : ""
  }`;
};

export const toBootstrapCouponFromStripe = (
  coupon: Stripe.Coupon | null | undefined,
): WebSignupBootstrapCoupon | null => {
  if (!coupon || !coupon.id) {
    return null;
  }

  return {
    id: coupon.id,
    amount_off:
      coupon.amount_off !== undefined && coupon.amount_off !== null
        ? coupon.amount_off
        : null,
    percent_off:
      coupon.percent_off !== undefined && coupon.percent_off !== null
        ? coupon.percent_off
        : null,
  };
};

/** Minimal Stripe.Coupon-shaped object for Elements amount + submit couponId. */
export const toStripeCouponFromBootstrap = (
  coupon: WebSignupBootstrapCoupon | null,
): Stripe.Coupon | null => {
  if (!coupon) {
    return null;
  }

  return {
    id: coupon.id,
    object: "coupon",
    amount_off: coupon.amount_off,
    percent_off: coupon.percent_off,
    created: 0,
    currency: "aud",
    duration: "once",
    duration_in_months: null,
    livemode: false,
    max_redemptions: null,
    metadata: {},
    name: null,
    redeem_by: null,
    times_redeemed: 0,
    valid: true,
  } as Stripe.Coupon;
};

/** Minimal ViewWLSubscriptionsTiers for mount + submit from bootstrap params. */
export const toViewSubscriptionFromBootstrap = (
  subscription: WebSignupBootstrapSubscription,
): ViewWLSubscriptionsTiers => {
  return {
    docId: subscription.docId,
    name: subscription.name,
    platform: "stripe",
    stripePriceId: subscription.stripePriceId,
    isOneOff: false,
    lockerPointsCost: 0,
    productQueryIdentifier: "",
    shortDescription: "",
    entriesPerMonth: subscription.entriesPerMonth,
    rewardPointsPerMonth: subscription.rewardPointsPerMonth,
    merchDiscount: 0,
    benefits: [],
    createdDt: 0,
    updatedDt: 0,
    price: subscription.price,
    priceString: formatDollarPriceDisplay(subscription.price),
    alternativePriceVersion: subscription.alternativePriceVersion,
  };
};
